import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../molecules/PageHeader';
import TextEdit from '../../molecules/TextEdit';
import FileUpload from '../../molecules/FileUpload';
import ActionButton from '../../molecules/ActionButton';
import Loading from '../../ions/Loading';
import { uploadInBatches, BatchUploadProgress } from '../../../services/BatchUploadService';
import { STRUCTURING_ROUTES, STRUCTURING_BREADCRUMBS } from './structuringUtils';
import { IoCheckmarkCircle, IoCloseCircle, IoTime } from 'react-icons/io5';
import {
    FormData,
    UploadState,
    DocumentResult,
    initialUploadState,
    validateForm,
    showValidationError,
    showSuccess,
    showError,
    findOperationByCode,
    fetchOperationStatus,
} from './OperationPageFormBackend';

const OperationPageForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({ operationName: '', files: [] });
    const [uploadState, setUploadState] = useState<UploadState>(initialUploadState);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const startPolling = useCallback((operationId: string, totalFiles: number) => {
        stopPolling();
        setUploadState(prev => ({
            ...prev,
            isPolling: true,
            operationId,
            totalFilesUploaded: totalFiles,
            operationStatus: 'processing',
            documentResults: [],
        }));

        pollingRef.current = setInterval(async () => {
            const result = await fetchOperationStatus(operationId);
            if (!result) return;

            setUploadState(prev => {
                const done = result.status !== 'processing';
                if (done) stopPolling();
                return {
                    ...prev,
                    documentResults: result.document_results,
                    operationStatus: result.status,
                    isPolling: !done,
                };
            });
        }, 3000);
    }, [stopPolling]);

    const handleSave = async () => {
        const validation = validateForm(formData);
        if (!validation.valid) { showValidationError(validation.error!); return; }

        try {
            setUploadState(prev => ({ ...prev, isProcessing: true, uploadProgress: null }));

            const result = await uploadInBatches(
                formData.operationName,
                formData.files,
                30,
                (progress: BatchUploadProgress) => {
                    setUploadState(prev => ({
                        ...prev,
                        uploadProgress: progress,
                        processingStatus: `Enviando lote ${progress.currentBatch} de ${progress.totalBatches}...`,
                    }));
                }
            );

            setUploadState(prev => ({
                ...prev,
                isProcessing: false,
                uploadProgress: null,
                operationCode: result.operation_code,
                summary: result.summary,
            }));

            if (result.success) {
                showSuccess('Upload concluído! Iniciando processamento...');
                // Find operation id then start polling
                const opId = await findOperationByCode(result.operation_code);
                if (opId) {
                    startPolling(opId, formData.files.length);
                } else {
                    showError('Não foi possível localizar a operação para acompanhar o processamento.');
                }
            } else {
                showError(`Upload parcialmente concluído. ${result.errors.length} lotes falharam.`);
            }
        } catch (err: any) {
            showError(err.message || 'Erro de conexão com o servidor');
            setUploadState(prev => ({ ...prev, isProcessing: false, uploadProgress: null }));
        }
    };

    const handleReset = () => {
        stopPolling();
        setUploadState(initialUploadState);
        setFormData({ operationName: '', files: [] });
    };

    const isFormDisabled = uploadState.isProcessing || uploadState.isPolling || uploadState.operationStatus === 'completed' || uploadState.operationStatus === 'failed';
    const pollingDone = !uploadState.isPolling && uploadState.operationId && uploadState.operationStatus && uploadState.operationStatus !== 'processing';
    const successCount = uploadState.documentResults.filter(d => d.status === 'success').length;
    const errorCount = uploadState.documentResults.filter(d => d.status === 'error').length;
    const processed = uploadState.documentResults.length;
    const total = uploadState.totalFilesUploaded;
    const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;

    const extractFileName = (filePath: string) => {
        const parts = filePath.split('/');
        return parts[parts.length - 1];
    };

    return (
        <div className="p-4 relative">
            {uploadState.isProcessing && <Loading />}
            <PageHeader
                title="Nova operação"
                breadcrumb={[STRUCTURING_BREADCRUMBS.structuring, STRUCTURING_BREADCRUMBS.newOperation]}
                apiVersion=""
            >
                {!isFormDisabled && (
                    <ActionButton
                        label={uploadState.isProcessing ? 'Enviando...' : 'Salvar'}
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => { if (!uploadState.isProcessing) void handleSave(); }}
                    />
                )}
            </PageHeader>

            <div className={`grid gap-4 mt-4 ${isFormDisabled ? 'pointer-events-none opacity-50' : ''}`}>
                <TextEdit
                    label="Nome da operação"
                    value={formData.operationName}
                    onChange={(e) => setFormData(prev => ({ ...prev, operationName: e.target.value }))}
                    disabled={isFormDisabled}
                />

                {uploadState.uploadProgress && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                {uploadState.processingStatus}
                            </span>
                            <span className="text-sm text-blue-600 dark:text-blue-400">
                                {uploadState.uploadProgress.processedFiles}/{uploadState.uploadProgress.totalFiles} arquivos
                            </span>
                        </div>
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-1">
                            <span>Lote {uploadState.uploadProgress.currentBatch}/{uploadState.uploadProgress.totalBatches}</span>
                            <span>{Math.round((uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100)}%</span>
                        </div>
                    </div>
                )}
            </div>

            {!isFormDisabled && (
                <div className="mt-4">
                    <FileUpload
                        accept=".xml"
                        name="files"
                        label="Arquivos"
                        errorMessage=""
                        onUploadFile={(uploadedFile) => setFormData(prev => ({ ...prev, files: [...prev.files, uploadedFile] }))}
                        onRemoveFile={() => setFormData(prev => ({ ...prev, files: [] }))}
                        disabled={isFormDisabled}
                        multiple={true}
                    />
                </div>
            )}

            {/* Polling progress section */}
            {(uploadState.isPolling || pollingDone) && (
                <div className="mt-6 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {uploadState.isPolling
                                ? `Processando documentos... (${processed}/${total})`
                                : `Processamento concluído`
                            }
                        </h3>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            Operação: {uploadState.operationCode}
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                        <div
                            className={`h-3 rounded-full transition-all duration-500 ${pollingDone ? (errorCount > 0 ? 'bg-amber-500' : 'bg-green-500') : 'bg-blue-500'}`}
                            style={{ width: `${pollingDone ? 100 : progressPct}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                        <span>{processed} de {total} documentos</span>
                        <span>{pollingDone ? '100' : progressPct}%</span>
                    </div>

                    {/* Summary when done */}
                    {pollingDone && (
                        <div className={`mb-4 p-3 rounded-lg ${errorCount > 0 ? 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-700' : 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-700'}`}>
                            <span className={`text-sm font-medium ${errorCount > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
                                {successCount} sucesso{successCount !== 1 ? 's' : ''}, {errorCount} erro{errorCount !== 1 ? 's' : ''} de {total} total
                            </span>
                        </div>
                    )}

                    {/* Document results list */}
                    <div className="max-h-80 overflow-y-auto space-y-1 border border-gray-100 dark:border-gray-700 rounded-lg">
                        {uploadState.documentResults.map((doc: DocumentResult, idx: number) => (
                            <div key={idx} className={`flex items-start gap-2 px-3 py-2 text-sm ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-slate-700/50' : ''}`}>
                                {doc.status === 'success' ? (
                                    <IoCheckmarkCircle className="text-green-500 w-5 h-5 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <IoCloseCircle className="text-red-500 w-5 h-5 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="min-w-0">
                                    <span className="font-medium text-gray-800 dark:text-gray-200">{extractFileName(doc.file)}</span>
                                    {doc.status === 'success' ? (
                                        <span className="text-gray-500 dark:text-gray-400"> — {doc.receivables_count} recebíve{doc.receivables_count !== 1 ? 'is' : 'l'}</span>
                                    ) : (
                                        <p className="text-red-600 dark:text-red-400 text-xs mt-0.5">{doc.error}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                        {/* Pending files */}
                        {uploadState.isPolling && Array.from({ length: Math.max(0, total - processed) }, (_, i) => (
                            <div key={`pending-${i}`} className={`flex items-center gap-2 px-3 py-2 text-sm ${(processed + i) % 2 === 0 ? 'bg-gray-50 dark:bg-slate-700/50' : ''}`}>
                                <IoTime className="text-gray-400 w-5 h-5 flex-shrink-0" />
                                <span className="text-gray-400 dark:text-gray-500">Aguardando...</span>
                            </div>
                        )).slice(0, 5)}
                        {uploadState.isPolling && total - processed > 5 && (
                            <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 text-center">
                                ... e mais {total - processed - 5} arquivo{total - processed - 5 !== 1 ? 's' : ''}
                            </div>
                        )}
                    </div>

                    {/* Action buttons when done */}
                    {pollingDone && (
                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => navigate(STRUCTURING_ROUTES.view(uploadState.operationId))}
                                className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white font-medium rounded text-sm px-5 py-2.5"
                            >
                                Ver Operação
                            </button>
                            <button
                                onClick={handleReset}
                                className="bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-medium rounded text-sm px-5 py-2.5"
                            >
                                Nova Operação
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OperationPageForm;
